import { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const projects = [
  {
    title: 'FinTrack Pro',
    category: 'Web Application',
    description: 'A comprehensive financial management platform with real-time analytics and automated reporting.',
    tech: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=80',
  },
  {
    title: 'MediConnect',
    category: 'Mobile Application',
    description: 'Cross-platform healthcare app enabling virtual consultations and secure medical record access.',
    tech: ['React Native', 'Firebase', 'Node.js', 'HIPAA'],
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&auto=format&fit=crop&q=80',
  },
  {
    title: 'LogiFlow',
    category: 'Custom Software',
    description: 'End-to-end logistics management system with real-time tracking and route optimization.',
    tech: ['Python', 'Django', 'Redis', 'Docker'],
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&auto=format&fit=crop&q=80',
  },
  {
    title: 'EduSphere',
    category: 'Web Platform',
    description: 'Interactive e-learning platform featuring live sessions and adaptive learning paths.',
    tech: ['Next.js', 'GraphQL', 'MongoDB', 'WebRTC'],
    image: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&auto=format&fit=crop&q=80',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.96 },
  visible: (i) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function PortfolioSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const navigate = useNavigate();
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: parallaxRef, offset: ['start end', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '5%']);

  return (
    <section id="portfolio" ref={parallaxRef} data-testid="portfolio-section" className="relative py-24 md:py-32 overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 bg-[#0a0f1e]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
      {/* Floating orbs */}
      <motion.div
        animate={{ y: [0, -15, 0], x: [0, 8, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute bottom-32 right-16 w-72 h-72 bg-indigo-500/5 rounded-full blur-[100px]"
      />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <span className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">Our Work</span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-white mb-4">
              Featured Projects
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-xl leading-relaxed">
              A selection showcasing our technical capability and design thinking.
            </p>
          </div>
          <Button
            data-testid="portfolio-explore-more"
            onClick={() => navigate('/portfolio')}
            className="bg-transparent border border-white/10 text-slate-300 hover:text-white hover:border-blue-500/40 hover:bg-blue-500/5 rounded-full px-6 h-10 text-sm font-medium transition-all duration-300 group self-start md:self-auto"
          >
            View All Projects
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.title}
              custom={index}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              variants={cardVariants}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              data-testid={`portfolio-card-${index}`}
              className="group relative rounded-2xl overflow-hidden border border-white/5 bg-slate-900/30 hover:border-blue-500/20 transition-all duration-500 cursor-pointer"
              onClick={() => navigate('/portfolio')}
            >
              <div className="relative h-56 overflow-hidden">
                <motion.img
                  whileHover={{ scale: 1.08 }}
                  transition={{ duration: 0.6 }}
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                  <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center">
                    <ExternalLink className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <span className="text-xs font-medium text-blue-400 tracking-wide uppercase">{project.category}</span>
                <h3 className="font-heading font-semibold text-xl text-white mt-2 mb-3">{project.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">{project.description}</p>
                <div className="flex flex-wrap gap-2">
                  {project.tech.map((t) => (
                    <span key={t} className="text-xs px-2.5 py-1 rounded-md bg-blue-500/10 text-blue-300 border border-blue-500/10">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
